import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Lock, RefreshCw, LogOut, Phone } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
      toast.success("Вхід виконано");
    } catch (err) {
      toast.error("Невірний пароль");
    } finally {
      setLoading(false);
    }
  };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Password": token };
      const [b, w] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/waitlist`, { headers }),
      ]);
      setBookings(b.data);
      setWaitlist(w.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        setToken("");
        toast.error("Сесія закінчилась");
      } else {
        toast.error("Помилка завантаження");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchAll();
  }, [token, fetchAll]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setBookings([]);
    setWaitlist([]);
  };

  const filtered = bookings.filter((b) => filter === "all" || b.payment_status === filter);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-[#FDFBF7] flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-white/10 p-8 bg-[#0E0E11]"
        >
          <Lock className="w-6 h-6 text-[#A01828] mb-6" strokeWidth={1.5} />
          <h1 className="font-serif-display text-3xl font-light mb-2">Адмін-панель</h1>
          <p className="text-white/50 text-sm mb-8">Grand Dance Studio</p>
          <label className="text-xs uppercase tracking-widest text-[#A19E98]">Пароль</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full mt-2 bg-[#141417] border border-white/10 text-[#FDFBF7] px-4 h-12 outline-none focus:border-[#7A1220]"
            placeholder="••••••••"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full mt-6 h-12 bg-[#7A1220] hover:bg-[#A01828] transition-colors text-white text-xs uppercase tracking-[0.2em] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Увійти"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#FDFBF7] px-6 sm:px-10 lg:px-16 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
          <div>
            <p className="overline">Адмін</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-light mt-3">
              Заявки на бронювання
            </h1>
            <p className="text-white/50 mt-2 text-sm">Всього: {bookings.length}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAll}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border border-white/15 px-4 h-10 text-xs uppercase tracking-widest hover:border-[#A01828] transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Оновити
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/15 px-4 h-10 text-xs uppercase tracking-widest hover:border-[#A01828] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Вихід
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-filters">
          {[
            { v: "all", l: "Всі" },
            { v: "paid", l: "Оплачені" },
            { v: "pending", l: "Очікують" },
            { v: "unpaid", l: "Не оплачені" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`filter-${f.v}`}
              className={`px-4 h-9 text-xs uppercase tracking-widest border transition-colors ${
                filter === f.v
                  ? "border-[#7A1220] bg-[#7A1220] text-white"
                  : "border-white/15 text-white/70 hover:border-white/30"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="admin-bookings-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0E0E11] text-white/60 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-4">Дата</th>
                <th className="text-left p-4">Ім'я</th>
                <th className="text-left p-4">Контакт</th>
                <th className="text-left p-4">Група</th>
                <th className="text-left p-4">Пакет</th>
                <th className="text-left p-4">Сума</th>
                <th className="text-left p-4">Статус</th>
                <th className="text-left p-4">Коментар</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center p-10 text-white/40">
                    Немає заявок
                  </td>
                </tr>
              )}
              {filtered.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                  data-testid={`booking-row-${b.id}`}
                >
                  <td className="p-4 text-white/60 whitespace-nowrap">
                    {new Date(b.created_at).toLocaleString("uk-UA", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="p-4 font-medium">{b.name}</td>
                  <td className="p-4 text-white/80">{b.contact}</td>
                  <td className="p-4 text-white/60">{b.preferred_group || "—"}</td>
                  <td className="p-4 text-white/60">{b.package_id}</td>
                  <td className="p-4 whitespace-nowrap">
                    {b.amount} {b.currency?.toUpperCase()}
                  </td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-1 text-[10px] uppercase tracking-widest ${
                        b.payment_status === "paid"
                          ? "bg-[#7A1220] text-white"
                          : "border border-white/15 text-white/60"
                      }`}
                    >
                      {b.payment_status}
                    </span>
                  </td>
                  <td className="p-4 text-white/50 max-w-xs truncate">{b.comment || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* WAITLIST — March enrollment for Anastasia's groups */}
        <div className="mt-14 mb-6" data-testid="admin-waitlist-section">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-[#A01828]" strokeWidth={1.5} />
            <h2 className="font-serif-display text-3xl sm:text-4xl font-light">
              Лист очікування · березень
            </h2>
          </div>
          <p className="text-white/50 mt-2 text-sm">
            Групи Анастасії заповнені. Ці жінки чекають на відкриття набору — всього: {waitlist.length}
          </p>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="admin-waitlist-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0E0E11] text-white/60 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-4">Дата</th>
                <th className="text-left p-4">Ім'я</th>
                <th className="text-left p-4">Телефон</th>
                <th className="text-left p-4">Коментар</th>
              </tr>
            </thead>
            <tbody>
              {waitlist.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center p-10 text-white/40">
                    Поки що нікого немає
                  </td>
                </tr>
              )}
              {waitlist.map((w) => (
                <tr
                  key={w.id}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                  data-testid={`waitlist-row-${w.id}`}
                >
                  <td className="p-4 text-white/60 whitespace-nowrap">
                    {new Date(w.created_at).toLocaleString("uk-UA", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="p-4 font-medium">{w.name}</td>
                  <td className="p-4 text-white/80 whitespace-nowrap">{w.phone}</td>
                  <td className="p-4 text-white/50 max-w-xs truncate">{w.comment || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
